import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal, Show } from 'solid-js'
import Badge from '~/components/Badge'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import Select from '~/components/Select'

const SelectPage: Component = () => {
  const [selectedCountry, setSelectedCountry] = createSignal<string | number>('')
  const [selectedTechs, setSelectedTechs] = createSignal<Array<string | number>>([])

  const countryOptions = [
    { value: 'co', title: 'Colombia', icon: 'material-symbols-light:flag-outline', subtitle: 'Bogotá' },
    { value: 'pe', title: 'Perú', icon: 'material-symbols-light:flag-outline', subtitle: 'Lima' },
    { value: 'mx', title: 'México', icon: 'material-symbols-light:flag-outline', subtitle: 'CDMX' },
    { value: 'ar', title: 'Argentina', icon: 'material-symbols-light:flag-outline', subtitle: 'Buenos Aires', disabled: true }
  ]

  const techOptions = [
    { value: 'solid', title: 'SolidJS', icon: 'material-symbols-light:science-outline' },
    { value: 'tailwind', title: 'Tailwind CSS', icon: 'material-symbols-light:palette-outline' },
    { value: 'bun', title: 'Bun', icon: 'material-symbols-light:bolt-outline' },
    { value: 'vite', title: 'Vite', icon: 'material-symbols-light:bolt-outline' }
  ]

  return (
    <>
      <Title>Select - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Select</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Interface de selección avanzada. El componente Select combina simplicidad con potencia, ofreciendo búsqueda (autocomplete), selección múltiple con chips, virtualización para grandes
            volúmenes de datos y soporte nativo para iconos.
          </p>
        </div>

        <div class="space-y-12">
          {/* Configuración Estándar */}
          <Card>
            <Card.Header
              title="Selección Unificada"
              subtitle="Variantes visuales y estados de interacción esenciales."
              icon={<Icon icon="material-symbols-light:checklist" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 p-8 bg-surface-muted rounded-2xl border border-border">
                <Select label="Ubicación Principal" options={countryOptions} value={selectedCountry()} onChange={setSelectedCountry} clearable />
                <Select label="Modo Deshabilitado" options={countryOptions} disabled placeholder="No disponible" />
                <Select label="Estado de Error" options={countryOptions} error="Selección requerida" />
              </div>
            </Card.Body>
          </Card>

          {/* Características Avanzadas */}
          <Card>
            <Card.Header
              title="Potencia Operativa"
              subtitle="Selección múltiple, búsqueda en tiempo real y virtualización de datos."
              icon={<Icon icon="material-symbols-light:code" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-10">
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-10">
                  <div class="space-y-4">
                    <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest pl-3 border-l-4 border-indigo-500">Multiselección con Chips</h4>
                    <Select label="Stack Tecnológico" options={techOptions} multiple visualized value={selectedTechs()} onChange={setSelectedTechs} placeholder="Aumenta tu stack..." />
                    <div class="flex flex-wrap gap-2 pt-2">
                      <Badge variant="soft" size="sm">
                        Count: {selectedTechs().length}
                      </Badge>
                      <Show when={selectedTechs().length > 0}>
                        <button onClick={() => setSelectedTechs([])} class="text-[10px] text-primary-500 hover:underline">
                          Limpiar selección
                        </button>
                      </Show>
                    </div>
                  </div>

                  <div class="space-y-4">
                    <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest pl-3 border-l-4 border-emerald-500">Búsqueda Predictiva</h4>
                    <Select label="Buscador de Países" options={countryOptions} autocomplete placeholder="Escribe para filtrar..." startIcon="material-symbols-light:search" />
                    <p class="text-[10px] text-foreground-subtle italic">Prueba buscando "Bogotá" o "México" para filtrar por título o subtítulo.</p>
                  </div>
                </div>

                <div class="p-6 bg-surface rounded-2xl border border-border">
                  <div class="flex items-center gap-4 mb-4">
                    <Icon icon="material-symbols-light:database-outline" class="text-amber-500" size={20} />
                    <h4 class="font-bold text-foreground">Virtualización nativa</h4>
                  </div>
                  <p class="text-sm text-foreground-subtle mb-6">
                    Maneja listas de miles de elementos sin degradación de rendimiento activando <code>virtualized</code>.
                  </p>
                  <Select
                    label="Dataset Masivo (Virtualized)"
                    options={Array.from({ length: 1000 }, (_, i) => ({
                      value: i.toString(),
                      title: `Registro Delta-${i}`
                    }))}
                    virtualized
                    placeholder="Explora 1,000 registros..."
                  />
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Especificaciones de configuración para el componente Select."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">options</td>
                      <td class="px-6 py-4 italic text-xs">OptionType[]</td>
                      <td class="px-6 py-4 font-mono">[]</td>
                      <td class="px-6 py-4">Array de objetos con {`{ value, title, subtitle, icon, disabled }`}.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">multiple</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4">Habilita selección múltiple y devuelve un array de valores.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-secondary-600">autocomplete</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 italic">Activa el motor de búsqueda interno.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">virtualized</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4">Renderizado inteligente para listas extensas.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">visualized</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 italic">Muestra chips dentro del input para selecciones múltiples.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default SelectPage
